import { Link } from 'react-router-dom'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const faqs = [
  {
    question: 'How do I import transcripts from Microsoft Teams?',
    answer:
      'Navigate to any project, click on "Recordings & Transcripts", then select "Import from Teams". You\'ll see a list of your recent Teams meetings. Click on a meeting to view available transcripts and import them directly into your project.',
  },
  {
    question: 'What file formats are supported for recording uploads?',
    answer:
      'We support MP3, MP4, WAV, M4A, WEBM, and OGG audio/video formats. Files should be under 2GB for optimal processing.',
  },
  {
    question: 'How long does transcription take?',
    answer:
      'Transcription typically takes 1-5 minutes per hour of audio. The status will update automatically - you can navigate away and come back to check progress.',
  },
  {
    question: 'What are Source Documents?',
    answer:
      'Source Documents are customer-provided files like PDFs, Word docs, PowerPoints, or images. These are indexed and can be included when you "Chat with Data" to provide additional context alongside your transcripts.',
  },
  {
    question: 'What\'s the difference between AI Templates and OG Templates?',
    answer:
      'AI Templates contain [[prompt]] placeholders that guide the AI to generate content for each section. OG (Original) Templates are traditional human-written document templates that serve as examples or starting points.',
  },
  {
    question: 'How does "Chat with Data" work?',
    answer:
      'Chat with Data uses AI to answer questions about your transcripts, source documents, and WAF-CAF audit reports. The AI searches across all your indexed content and provides answers with source citations. Ask about security findings, compliance issues, infrastructure details, and more.',
  },
  {
    question: 'How do I generate documents?',
    answer:
      'Go to your project, click "Generated Documents", then "Generate Document". Select a template, and the AI will use your transcripts and source documents to populate each section automatically.',
  },
  {
    question: 'Can I edit generated documents?',
    answer:
      'Generated documents are downloaded as Word files (.docx) that you can edit freely in Microsoft Word or any compatible application.',
  },
]

export default function Help() {
  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
          <i className="fa-solid fa-life-ring text-4xl" style={{ color: '#60a5fa' }}></i>
        </div>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Help & FAQ</h1>
        <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
          Find answers to common questions or reach out for support
        </p>
      </div>

      {/* Contact Card */}
      <div className="rounded-xl p-8"
        style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
        <div className="flex items-start space-x-4">
          <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(100, 180, 255, 0.3)', boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)' }}>
            <i className="fa-solid fa-headset text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Need Help?</h2>
            <p className="mb-4" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Have questions, feedback, or need assistance? Reach out directly via Microsoft Teams.
            </p>
            <a
              href="https://teams.microsoft.com/l/chat/0/0?users=jeff.blakely@coretek.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.2 6.4h-4.8v4.8h4.8V6.4zm-14.4 0v4.8h4.8V6.4H4.8zm9.6 9.6v4.8h4.8V16h-4.8zM4.8 16v4.8h4.8V16H4.8zm4.8-4.8H4.8v4.8h4.8v-4.8zm4.8 0h-4.8v4.8h4.8v-4.8zm4.8 0h-4.8v4.8h4.8v-4.8zM9.6 6.4H4.8v4.8h4.8V6.4zm4.8 0H9.6v4.8h4.8V6.4z"/>
              </svg>
              Message Jeff Blakely on Teams
            </a>
            <p className="text-sm mt-3" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              <i className="fa-solid fa-envelope mr-1"></i>
              jeff.blakely@coretek.com
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/projects"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] group"
          style={{ ...glassPanel, borderColor: 'rgba(52, 211, 153, 0.2)' }}
        >
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
            style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            <i className="fa-solid fa-folder text-xl" style={{ color: '#34d399' }}></i>
          </div>
          <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>View Projects</h3>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Manage your projects</p>
        </Link>
        <Link
          to="/templates"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] group"
          style={{ ...glassPanel, borderColor: 'rgba(139, 92, 246, 0.2)' }}
        >
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
            style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
            <i className="fa-solid fa-robot text-xl" style={{ color: '#a78bfa' }}></i>
          </div>
          <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>AI Templates</h3>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Browse document templates</p>
        </Link>
        <Link
          to="/"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] group"
          style={{ ...glassPanel, borderColor: 'rgba(59, 130, 246, 0.2)' }}
        >
          <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
            style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <i className="fa-solid fa-chart-line text-xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>Dashboard</h3>
          <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>View activity overview</p>
        </Link>
      </div>

      {/* FAQ Section */}
      <div className="rounded-xl" style={glassPanel}>
        <div className="p-6" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
          <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>Frequently Asked Questions</h2>
        </div>
        <div>
          {faqs.map((faq, index) => (
            <details key={index} className="group" style={{ borderBottom: index < faqs.length - 1 ? '1px solid rgba(100, 180, 255, 0.1)' : 'none' }}>
              <summary className="p-6 cursor-pointer transition flex items-center justify-between"
                style={{ color: '#e2e8f0' }}>
                <span className="font-medium pr-4">{faq.question}</span>
                <i className="fa-solid fa-chevron-down group-open:rotate-180 transition-transform" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
              </summary>
              <div className="px-6 pb-6 pt-0" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>

      {/* Version Info */}
      <div className="text-center text-sm" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
        <p>BreezyDoc by Coretek</p>
        <p className="mt-1">
          Built with <i className="fa-solid fa-heart" style={{ color: '#f87171' }}></i> using Azure AI
        </p>
      </div>
    </div>
  )
}
